/**
 * Scroll reveals: page sections that start below the screen fade in and rise slightly as they scroll into view.
 *
 * - Only sections of the main content are animated, never the first one; ones already on screen when the page
 *   loads are left alone, so nothing flickers and the first thing people see is never delayed.
 * - Once a section has appeared its classes are removed, so it has no leftover transform (a transform would break
 *   sticky and fixed elements inside it, such as a sticky add-to-cart bar).
 * - Off in the theme editor, for anyone who asks their device to reduce motion, and when the page loads without a
 *   visible size (so there is no way to tell what is on screen).
 */

const HIDDEN = 'sr-hidden';
const SHOWN = 'sr-shown';

const style = document.createElement('style');
style.textContent = `
  .${HIDDEN} {
    opacity: 0;
    transform: translateY(28px);
  }

  .${SHOWN} {
    opacity: 1;
    transform: none;
    transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.2, 0.7, 0.2, 1);
  }
`;

function setUp() {
  if (window.Shopify?.designMode) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.innerHeight === 0) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  document.head.append(style);

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const section = /** @type {HTMLElement} */ (entry.target);
        observer.unobserve(section);
        section.classList.add(SHOWN);
        section.classList.remove(HIDDEN);
        section.addEventListener('transitionend', () => section.classList.remove(SHOWN), { once: true });
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
  );

  /** @param {HTMLElement} section @param {number} index */
  const watch = (section, index) => {
    if (section.dataset.srWatched) return;
    section.dataset.srWatched = 'true';
    // Leave the first section and anything already on screen as they are.
    if (index === 0 || section.getBoundingClientRect().top < window.innerHeight) return;
    section.classList.add(HIDDEN);
    observer.observe(section);
  };

  const watchAll = () =>
    main.querySelectorAll(':scope > .shopify-section').forEach((section, index) => watch(/** @type {HTMLElement} */ (section), index));
  watchAll();

  // Page transitions can swap the main content; pick up the new sections.
  new MutationObserver(watchAll).observe(main, { childList: true });
}

setUp();